import React, { useMemo } from "react";
import { DayPicker } from "react-day-picker";
import { es } from "date-fns/locale";
import { Typography } from "@material-tailwind/react";
import {
  parseYmdAsLocalNoon,
  dateToYmd,
} from "../../utils/bookingWindowMx";
import {
  filterAppointmentsInMxMonth,
  formatMxLong,
  formatTimeMxOnly,
  todayMxYmd,
  utcToMxYmd,
} from "../../utils/dateMx";
import { labelAppointmentModality } from "../../utils/consultationModes";
import {
  appointmentStatusPrefix,
  clientDisplayName,
  groupAppointmentsByMxDate,
  isAppointmentCancelled,
} from "../../utils/psychAppointments";

import "react-day-picker/style.css";

export default function PsychologistAppointmentsCalendar({
  appointments = [],
  selectedYmd,
  onSelectYmd,
  displayMonth,
  onDisplayMonthChange,
}) {
  const byDate = useMemo(
    () => groupAppointmentsByMxDate(appointments),
    [appointments]
  );

  const selected = selectedYmd ? parseYmdAsLocalNoon(selectedYmd) : undefined;
  const monthDate = displayMonth || selected || new Date();

  const monthAppointments = useMemo(() => {
    const y = monthDate.getFullYear();
    const m = monthDate.getMonth() + 1;
    return filterAppointmentsInMxMonth(appointments, y, m).sort((a, b) =>
      String(a.startUtc).localeCompare(String(b.startUtc))
    );
  }, [appointments, monthDate]);

  const selectedDayAppointments = useMemo(() => {
    if (!selectedYmd) return [];
    return byDate.get(selectedYmd) || [];
  }, [byDate, selectedYmd]);

  const daysWithScheduled = useMemo(
    () =>
      [...byDate.entries()]
        .filter(([, list]) => list.some((a) => !isAppointmentCancelled(a)))
        .map(([ymd]) => parseYmdAsLocalNoon(ymd)),
    [byDate]
  );

  const daysWithCancelled = useMemo(
    () =>
      [...byDate.entries()]
        .filter(([, list]) => list.every((a) => isAppointmentCancelled(a)))
        .map(([ymd]) => parseYmdAsLocalNoon(ymd)),
    [byDate]
  );

  const daysMixed = useMemo(
    () =>
      [...byDate.entries()]
        .filter(
          ([, list]) =>
            list.some((a) => isAppointmentCancelled(a)) &&
            list.some((a) => !isAppointmentCancelled(a))
        )
        .map(([ymd]) => parseYmdAsLocalNoon(ymd)),
    [byDate]
  );

  function handleSelect(d) {
    onSelectYmd(d ? dateToYmd(d) : "");
  }

  return (
    <div className="grid lg:grid-cols-[minmax(0,22rem)_1fr] gap-6 lg:gap-8 items-start">
      <div className="psych-appt-calendar rounded-2xl border border-brand-brown/15 bg-white p-3 sm:p-5 shadow-sm min-w-0 overflow-hidden">
        <DayPicker
          mode="single"
          locale={es}
          weekStartsOn={1}
          month={monthDate}
          onMonthChange={onDisplayMonthChange}
          selected={selected}
          onSelect={handleSelect}
          modifiers={{
            hasScheduled: daysWithScheduled,
            hasCancelledOnly: daysWithCancelled,
            hasMixed: daysMixed,
          }}
          modifiersClassNames={{
            selected: "!bg-brand-lavender !text-white !font-semibold hover:!bg-brand-lavender",
            today: "!font-bold !text-brand-lavender",
            hasScheduled: "psych-cal-day-scheduled",
            hasCancelledOnly: "psych-cal-day-cancelled",
            hasMixed: "psych-cal-day-mixed",
          }}
          className="mx-auto w-full"
          classNames={{
            months: "flex flex-col w-full",
            month: "relative w-full space-y-2",
            month_caption:
              "flex h-10 items-center justify-center px-10 font-sans text-base sm:text-lg text-brand-dark font-semibold capitalize",
            nav: "absolute inset-x-0 top-0 z-10 flex items-center justify-between px-0.5 sm:px-1",
            button_previous:
              "inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-brand-brown/20 bg-white text-brand-dark hover:bg-brand-lavender/10",
            button_next:
              "inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-brand-brown/20 bg-white text-brand-dark hover:bg-brand-lavender/10",
            weekdays: "grid grid-cols-7 gap-0.5 sm:gap-1 text-center",
            weekday:
              "text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-brand-gray py-1",
            week: "grid grid-cols-7 gap-0.5 sm:gap-1 mt-1",
            day: "flex justify-center p-0 relative",
            day_button:
              "h-8 w-8 sm:h-10 sm:w-10 rounded-full text-xs sm:text-sm font-medium text-brand-dark hover:bg-brand-lavender/15 transition-colors",
            outside: "opacity-45 text-brand-gray",
          }}
        />
      </div>

      <div className="min-w-0 space-y-6">
        <section>
          <Typography className="font-semibold text-brand-dark mb-3">
            {selectedYmd ? `Citas del ${selectedYmd}` : "Selecciona un día"}
          </Typography>
          {selectedDayAppointments.length === 0 ? (
            <p className="text-sm text-brand-gray rounded-xl border border-dashed border-brand-brown/25 bg-white/70 px-4 py-8 text-center">
              {selectedYmd
                ? "No hay citas en este día."
                : "Toca un día del calendario para ver el detalle."}
            </p>
          ) : (
            <ul className="space-y-3">
              {selectedDayAppointments.map((a) => (
                <AppointmentChip key={a.appointmentId} appt={a} detailed />
              ))}
            </ul>
          )}
        </section>

        <section>
          <Typography className="font-semibold text-brand-dark mb-3">
            Mes en curso ({monthAppointments.length})
          </Typography>
          {monthAppointments.length === 0 ? (
            <p className="text-sm text-brand-gray">Sin citas en este mes.</p>
          ) : (
            <ul className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
              {monthAppointments.map((a) => (
                <li key={a.appointmentId}>
                  <button
                    type="button"
                    onClick={() => onSelectYmd(utcToMxYmd(a.startUtc))}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition-colors hover:border-brand-lavender/40 hover:bg-brand-lavender/5 ${
                      isAppointmentCancelled(a)
                        ? "border-stone-200 bg-stone-50/80"
                        : "border-brand-brown/15 bg-white"
                    }`}
                  >
                    <AppointmentChip appt={a} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function AppointmentChip({ appt, detailed = false }) {
  const cancelled = isAppointmentCancelled(appt);
  const prefix = appointmentStatusPrefix(appt);
  const name = clientDisplayName(appt);

  return (
    <div className={detailed ? "rounded-xl border border-brand-brown/15 bg-white px-4 py-3 shadow-sm" : ""}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          className={`font-semibold tabular-nums ${
            cancelled ? "text-stone-500 line-through decoration-red-400/80" : "text-brand-dark"
          }`}
        >
          <span className={cancelled ? "text-red-500 no-underline" : ""} aria-hidden>
            {prefix}
          </span>
          {formatTimeMxOnly(appt.startUtc)}
        </span>
        <span
          className={`text-sm ${
            cancelled ? "text-stone-500 line-through decoration-stone-400" : "text-brand-dark"
          }`}
        >
          {name}
        </span>
        {appt.modality && (
          <span className="text-xs text-brand-gray">
            · {labelAppointmentModality(appt.modality)}
          </span>
        )}
        {cancelled && (
          <span className="text-xs font-medium text-red-600/90">Cancelada</span>
        )}
      </div>
      {detailed && appt.clientEmail && (
        <p className="text-xs text-brand-gray mt-1 break-all">{appt.clientEmail}</p>
      )}
      {detailed && (
        <p className="text-xs text-brand-gray mt-1">{formatMxLong(appt.startUtc)}</p>
      )}
    </div>
  );
}

export function defaultCalendarSelectedYmd() {
  return todayMxYmd();
}

export function PsychologistCalendarLegend() {
  return (
    <p className="text-xs text-brand-gray flex flex-wrap items-center gap-x-4 gap-y-1">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-brand-lavender font-medium" aria-hidden>
          ●
        </span>
        Programadas
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-red-500 font-medium" aria-hidden>
          ✕
        </span>
        Canceladas
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-brand-lavender font-medium tracking-tighter" aria-hidden>
          ●✕
        </span>
        Programadas y canceladas
      </span>
    </p>
  );
}
